import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ─────────────────────────────────────────────────────────────────────────────
// Login-time re-consent gate (SD-RECONSENT-GATE).
//
// Detection: on a returning farmer, compare the version recorded across their
// consent_records against the CURRENT active Terms version (country-row first,
// else the global row — the same precedence as the app's legalDocs resolver). If
// the farmer has no consent records, or their recorded version is older than the
// active version, the gate fires and routes them to the re-consent notice screen
// (which reuses the signup purpose notice + audio + legal links).
//
// BLOCKING MODE — SINGLE SWITCH, DEFAULT SOFT, pending Adeel's ruling:
//   'soft' — the notice is dismissible ("Skip for now"); after SOFT_HARD_AFTER
//            dismissals it becomes hard (skip disappears).
//   'hard' — the farmer cannot use the app until they re-accept (mirrors the
//            signup blocking gate and the is_onboarded gate).
// Flip to hard by changing this one constant. See result .md — Adeel confirms
// hard vs soft before this consent series is promoted (HOLD 2026-07-17).
// ─────────────────────────────────────────────────────────────────────────────
export const RECONSENT_BLOCKING_MODE: 'soft' | 'hard' = 'soft';

/** Soft mode: number of "Skip for now" dismissals allowed before the gate turns
 *  hard for this farmer (the skip button then disappears). */
export const SOFT_HARD_AFTER = 3;

const skipKey = (userId: string) => `agxl_reconsent_skips_${userId}`;
const dismissedKey = (userId: string) => `agxl_reconsent_dismissed_${userId}`;

export function softSkipCount(userId: string): number {
  const n = parseInt(localStorage.getItem(skipKey(userId)) || '0', 10);
  return Number.isNaN(n) ? 0 : n;
}

/** Hard NOW = configured hard, OR soft budget exhausted for this farmer. */
export function isReConsentHard(userId: string): boolean {
  return RECONSENT_BLOCKING_MODE === 'hard' || softSkipCount(userId) >= SOFT_HARD_AFTER;
}

/** Soft-mode dismissal: does not re-prompt again within this browser session. */
export function isSessionDismissed(userId: string): boolean {
  return sessionStorage.getItem(dismissedKey(userId)) === '1';
}

/** Record a "Skip for now": bumps the per-farmer counter (drives the soft→hard
 *  transition across logins) and suppresses re-prompting for this session. */
export function recordSoftSkip(userId: string): void {
  localStorage.setItem(skipKey(userId), String(softSkipCount(userId) + 1));
  sessionStorage.setItem(dismissedKey(userId), '1');
}

/** Clear all gate state for a farmer once they have re-accepted. */
export function clearReConsentState(userId: string): void {
  localStorage.removeItem(skipKey(userId));
  sessionStorage.removeItem(dismissedKey(userId));
}

const norm = (v: string | null | undefined) => (v ?? '').trim();

export interface ReConsentStatus {
  needsReConsent: boolean;
  isLoading: boolean;
  currentVersion: string | null;
  farmerVersion: string | null;
}

/**
 * Resolve whether the farmer must re-consent. Fails OPEN (needsReConsent=false)
 * if the active Terms version cannot be resolved, so a resolver error never
 * locks farmers out of the app.
 * FIX-SEASON-GATE-3 (E1): needsReConsent is true ONLY from a successful read. A failed
 * read (offline, error) throws and is never mistaken for "no consent records".
 */
export function useReConsentStatus(userId: string | undefined, countryId?: string | null): ReConsentStatus {
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['reconsent-status', userId, countryId ?? null],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ needs: boolean; current: string | null; farmer: string | null }> => {
      // 1) Active Terms version (country row first, else global row).
      const { data: termsRows, error: termsError } = await supabase
        .from('legal_documents')
        .select('country_id, version')
        .eq('doc_type', 'terms')
        .eq('is_active', true);
      if (termsError) throw termsError;
      let currentVersion: string | null = null;
      if (termsRows && termsRows.length > 0) {
        const countryRow = countryId ? termsRows.find((r) => r.country_id === countryId) : undefined;
        const globalRow = termsRows.find((r) => r.country_id === null);
        currentVersion = (countryRow?.version ?? globalRow?.version ?? null) as string | null;
      }
      // Fail open — cannot resolve the active version → do not gate.
      if (!currentVersion) return { needs: false, current: null, farmer: null };

      // 2) Farmer's recorded consent versions (RLS: session-token bound → own rows).
      const { data: rows, error: rowsError } = await supabase
        .from('consent_records')
        .select('consent_version')
        .eq('user_id', userId!);
      if (rowsError) throw rowsError;

      if (!rows || rows.length === 0) {
        // No records at all → must (re-)consent.
        return { needs: true, current: currentVersion, farmer: null };
      }
      const farmerMax = rows
        .map((r) => norm((r as { consent_version: string | null }).consent_version))
        .filter(Boolean)
        .sort()
        .pop() ?? null;

      // Version strings are 'YYYY-MM-DD' (or legacy '1.0'); lexicographic compare
      // is correct for both ('1.0' < any ISO date, older date < newer date).
      const needs = !farmerMax || farmerMax < currentVersion;
      return { needs, current: currentVersion, farmer: farmerMax };
    },
  });

  return {
    needsReConsent: isSuccess && data?.needs === true,
    isLoading,
    currentVersion: data?.current ?? null,
    farmerVersion: data?.farmer ?? null,
  };
}
